import type { FC } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';
import { classNames } from '../../utils/tailwind/classNames';
import { separateScriptTags } from '../utils';
import styles from './HtmlSnippet.module.scss';

export type HtmlSnippetProps = {
    id: string;
    html: string;
};

/**
 * This component is used to render HTML snippets that contain script tags.
 * Unlike dangerouslySetInnerHTML which only renders the HTML but does not execute scripts,
 * this component properly executes any script tags in the HTML by using createContextualFragment.
 * dangerouslySetInnerHTML is not sufficient here because it treats scripts as text content
 * rather than executable code.
 */
const HtmlSnippet: FC<HtmlSnippetProps> = memo(({ id, html }: HtmlSnippetProps) => {
    const renderedScripts = useRef<string>('');
    const elRef = useRef<HTMLDivElement>(null);
    const { html: htmlWithoutScriptTags, scriptTags } = useMemo(
        () => separateScriptTags(html),
        [html],
    );

    useEffect(() => {
        const scriptTagsString = scriptTags.join('');
        if (scriptTagsString === renderedScripts.current) return;
        const el = elRef.current;
        // remove any script tags that are already rendered
        el?.querySelectorAll('script')?.forEach((script) => {
            script.remove();
        });
        if (!scriptTags.length) return;
        const documentFragment = document.createRange().createContextualFragment(scriptTagsString);
        el?.append(documentFragment);
        renderedScripts.current = scriptTagsString;
    }, [scriptTags]);

    return (
        <div
            ref={elRef}
            className={classNames(styles.container, id)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                // executing the scripts by the browser generates react hydration problems
                // we were concatenating the scripts to allow the browser to execute them
                // but this causes issues with react hydration
                // so we are now using the createContextualFragment to execute the scripts
                // and we are not concatenating the scripts
                __html: htmlWithoutScriptTags,
            }}
            data-testid={id}
        />
    );
});

HtmlSnippet.displayName = 'HtmlSnippet';

export { HtmlSnippet };
